<script setup lang="ts">
/**
 * A colour for a cell: one of a few, none, or any.
 *
 * Presets first, because a table in a report wants one of a handful of pale
 * tints and the same handful every time; a "none" swatch, because taking a
 * colour off has to be as easy as putting one on; and the browser's own
 * picker last, for the one row somebody wants in the company's exact green.
 *
 * Nothing typed here reaches the document unvalidated. The picker yields a
 * six-digit hex by construction, and the presets are constants.
 */
defineProps<{
  label: string
  options: readonly { readonly id: string; readonly colour: string; readonly name: string }[]
  /** The colour every targeted cell shares, or none. */
  chosen: string | undefined
  noneLabel: string
  customLabel: string
  /** The label above the swatches, as an inspector field's is, rather than beside them. */
  stacked?: boolean | undefined
}>()

const emit = defineEmits<{
  /** A colour, or `null` to take the colour off. */
  choose: [colour: string | null]
}>()
</script>

<template>
  <div :class="['colour', { 'colour--stacked': stacked }]">
    <span :class="stacked ? 'b-label' : 'colour__label'">{{ label }}</span>

    <div class="colour__swatches" role="radiogroup" :aria-label="label">
      <button
        type="button"
        class="swatch swatch--none"
        :class="{ 'swatch--on': chosen === undefined }"
        :aria-pressed="chosen === undefined"
        :title="noneLabel"
        @click="emit('choose', null)"
      >
        <span class="swatch__fill" />
        <span class="visually-hidden">{{ noneLabel }}</span>
      </button>

      <button
        v-for="option in options"
        :key="option.id"
        type="button"
        class="swatch"
        :class="{ 'swatch--on': chosen === option.colour }"
        :style="{ '--swatch': option.colour }"
        :aria-pressed="chosen === option.colour"
        :title="option.name"
        @click="emit('choose', option.colour)"
      >
        <span class="swatch__fill" />
        <span class="visually-hidden">{{ option.name }}</span>
      </button>

      <input
        class="colour__well"
        type="color"
        :value="chosen ?? '#ffffff'"
        :title="customLabel"
        :aria-label="customLabel"
        @input="emit('choose', ($event.target as HTMLInputElement).value)"
      />
    </div>
  </div>
</template>

<style scoped>
.colour {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
}

.colour--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: var(--sp-3);
}

.colour__label {
  flex: none;
  width: 48px;
  color: var(--ink-soft);
  font-size: var(--text-xs);
}

.colour__swatches {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
}

.swatch {
  display: flex;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-xs);
  background: none;
  cursor: pointer;
  transition: border-color var(--quick);
}

.swatch:hover:not(:disabled) {
  border-color: var(--ink-faint);
}

.swatch--on {
  border-color: var(--accent);
  /* A ring rather than a thicker border, so the colour does not move by a
     pixel each time the selection changes. */
  box-shadow: 0 0 0 2px var(--accent-quiet);
}

.swatch:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.swatch__fill {
  width: 100%;
  height: 100%;
  margin: 2px;
  border-radius: 2px;
  background: var(--swatch);
}

/* "None": a diagonal through an empty well, the sign every colour picker
   uses for it. */
.swatch--none .swatch__fill {
  background: linear-gradient(
    to top right,
    transparent calc(50% - 1px),
    var(--danger) calc(50% - 1px),
    var(--danger) calc(50% + 1px),
    transparent calc(50% + 1px)
  );
}

/* The browser's own colour well, trimmed of its chrome so it sits with the
   swatches rather than looking like a form control that wandered in. */
.colour__well {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px dashed var(--hairline-strong);
  border-radius: var(--radius-xs);
  background: none;
  cursor: pointer;
  flex: none;
}

.colour__well::-webkit-color-swatch-wrapper {
  padding: 2px;
}

.colour__well::-webkit-color-swatch {
  border: 0;
  border-radius: 2px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
